import React, { useState, useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { formatPrice, getCurrencySymbol } from '../utils/formatPrice';
import { OrderProduct } from '../interfaces';
import './itemActions.css';

interface SwipeableOrderItemProps {
    prod: OrderProduct;
    index: number;
    showDelete: { [key: number]: boolean };
    setShowDelete: React.Dispatch<React.SetStateAction<{ [key: number]: boolean }>>;
    swipedDistance: number;
    setSwipedDistance: React.Dispatch<React.SetStateAction<number>>;
    handleLongPress: (index: number) => void;
    handleCancelLongPress: () => void;
    onDelete: (index: number) => void;
    resetShowDeleteCombination: () => void;
    displayCheckbox: boolean;
    checkboxState: boolean;
    onToggleCheckbox: () => void;
    backgroundColor: string;
    mealPlanStatus?: 'included' | 'extra' | 'none';
    /** Optional meaningful swipe-right / context-menu action: add a note to this line. */
    onAddNote?: (index: number) => void;
}

const SwipeableOrderItem: React.FC<SwipeableOrderItemProps> = ({
    index,
    prod,
    showDelete,
    setShowDelete,
    swipedDistance,
    setSwipedDistance,
    handleLongPress,
    handleCancelLongPress,
    onDelete,
    resetShowDeleteCombination,
    onToggleCheckbox,
    displayCheckbox,
    checkboxState,
    backgroundColor,
    mealPlanStatus,
    onAddNote,
}) => {
    const menuRef = useRef<HTMLDivElement>(null);
    // Local reveal state for the swipe-right "Note" action (mirrors the
    // parent-owned swipe-left "Delete" reveal).
    const [showNote, setShowNote] = useState(false);

    const handleSwipeLeft = () => {
        setShowNote(false);
        resetShowDeleteCombination();
        setShowDelete({ [index]: true });
    };

    const handleSwipeRight = () => {
        if (!onAddNote) return;
        resetShowDeleteCombination();
        setShowDelete({});
        setSwipedDistance(0);
        setShowNote(true);
    };

    const handlers = useSwipeable({
        onSwipedLeft: (eventData) => {
            const swipeDistance = eventData.deltaX;
            if (swipeDistance <= 0) handleSwipeLeft();
        },
        onSwipedRight: (eventData) => {
            if (eventData.deltaX > 0) handleSwipeRight();
        },
        onSwiping: (eventData) => {
            const swipeDistance = eventData.deltaX;
            if (swipeDistance >= 0) return; // right drag is handled on release
            setShowNote(false);
            if (swipeDistance < -63) {
                setSwipedDistance(-63);
            } else {
                setSwipedDistance(swipeDistance);
            }
        },
    });

    const getDiscountValueFormatted = (orderProduct: OrderProduct) => {
        const discountValue = getDiscountValue(orderProduct);
        return discountValue.toFixed(2);
    };

    const getDiscountValue = (orderProduct: OrderProduct) => {
        if (orderProduct.discount && orderProduct.discount != 0) {
            const productTotalPrice = calculateProductTotalPrice(orderProduct);
            if (orderProduct.discountType == "amount")
                return orderProduct.discount;
            else if (orderProduct.discountType == "percentage")
                return (orderProduct.discount / 100) * productTotalPrice;
        }
        return 0;
    }

    // Display unit price: a selected variant's price REPLACES the product base price.
    // Addons are rendered as their own lines, so they are NOT included here.
    const getDisplayUnitPrice = (op: OrderProduct) => {
        const pv = op.variants?.find(v => v?.priceWithoutTax != null && v.priceWithoutTax !== 0);
        return pv?.priceWithoutTax ?? op.product.price;
    };

    const calculateProductTotalPrice = (orderProduct: OrderProduct) => {
        // A selected variant's price REPLACES the base product price (not additive).
        const pricedVariant = orderProduct.variants?.find(v => v?.priceWithoutTax != null && v.priceWithoutTax !== 0);
        let totalPrice = pricedVariant?.priceWithoutTax ?? orderProduct.price;

        orderProduct.addons.forEach(addon => {
            if (addon.addon.priceWithoutTax) {
                totalPrice += addon.addon.priceWithoutTax * addon.qty;
            }
        });

        return totalPrice;
    };

    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const [showMenu, setShowMenu] = useState(false);
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        const rightOffset = windowSize.width - event.pageX - 70;
        setMenuPosition({ x: rightOffset > windowSize.width * 0.4 ? windowSize.width * 0.4 : rightOffset < 0 ? 0 : rightOffset, y: event.pageY });
        setShowMenu(true);
    };


    useEffect(() => {
        const handleResize = () => {
            console.log("onresize: innerWidth: " + window.innerWidth + "innerHeight: " + innerHeight)
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.onresize = handleResize;

        return () => {
            window.onresize = null;
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                });
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const updateSize = () => {
            console.log("update size")
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", updateSize);
        window.addEventListener("focus", updateSize);
        document.addEventListener("visibilitychange", updateSize);

        return () => {
            window.removeEventListener("resize", updateSize);
            window.removeEventListener("focus", updateSize);
            document.removeEventListener("visibilitychange", updateSize);
        };
    }, []);

    const handleCloseMenu = () => {
        setShowMenu(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                handleCloseMenu();
            }
        };

        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMenu]);

    const handleDelete = () => {
        onDelete(index);
        setShowMenu(false);
    }

    return (
        <div
            {...handlers}
            key={index}
            data-index={index}
            className='d-flex gap-0 to-item'
            onContextMenu={handleRightClick}
            onClick={() => { if (showNote) setShowNote(false); }}
            style={{
                cursor: 'pointer',
                borderTop: '1px solid #EAECF0',
                position: 'relative',
                transition: showDelete[index] ? 'none' : 'left 0.2s ease',
                left: showDelete[index]
                    ? swipedDistance >= -63 && swipedDistance < 0
                        ? swipedDistance
                        : 0
                    : showNote
                        ? 63
                        : 0,
            }}
        >
            {showMenu && (
                <div
                    className='tia-ctx'
                    ref={menuRef}
                    style={{ top: `20px`, right: `${menuPosition.x}px` }}
                    onClick={(e) => e.stopPropagation()}>
                    <button className='tia-ctxitem' onClick={() => { setShowMenu(false); handleLongPress(index); }}>
                        <svg width="16" height="16" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1.876 17.116c.046-.414.069-.62.131-.814.056-.171.134-.334.234-.485.111-.169.258-.316.553-.61L16 2.001a2.828 2.828 0 1 1 4 4L6.794 19.206c-.295.295-.442.442-.611.553-.15.1-.314.178-.485.234-.193.062-.4.085-.813.131L1.5 20.5l.376-3.384Z" /></svg>
                        Options
                    </button>
                    {onAddNote && (
                        <button className='tia-ctxitem' onClick={() => { setShowMenu(false); onAddNote(index); }}>
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2H5.8C4.12 2 3.28 2 2.638 2.327A3 3 0 0 0 1.327 3.638C1 4.28 1 5.12 1 6.8v7.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C3.28 19 4.12 19 5.8 19h7.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C18 16.72 18 15.88 18 14.2V11M18.121 1.879a3 3 0 0 1 0 4.243A3 3 0 1 1 13.88 1.88a3 3 0 0 1 4.242 0Z" /></svg>
                            Add note
                        </button>
                    )}
                    <div className='tia-ctx__div' style={{ height: 1, background: '#eee', margin: '4px 8px' }} />
                    <button className='tia-ctxitem tia-ctxitem--danger' onClick={() => handleDelete()}>
                        <svg width="16" height="16" viewBox="0 0 20 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 5V4.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C12.48 1 11.92 1 10.8 1H9.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C6 2.52 6 3.08 6 4.2V5M8 10.5v5M12 10.5v5M1 5h18M17 5v11.2c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C14.72 21 13.88 21 12.2 21H7.8c-1.68 0-2.52 0-3.162-.327a3 3 0 0 1-1.311-1.311C3 18.72 3 17.88 3 16.2V5" /></svg>
                        Delete line
                    </button>
                </div>
            )}

            {showNote && (
                <button
                    className='tia-reveal tia-reveal--note'
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowNote(false);
                        onAddNote?.(index);
                    }}
                    style={{ left: -63 }}
                >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2H5.8C4.12 2 3.28 2 2.638 2.327A3 3 0 0 0 1.327 3.638C1 4.28 1 5.12 1 6.8v7.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C3.28 19 4.12 19 5.8 19h7.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C18 16.72 18 15.88 18 14.2V11M18.121 1.879a3 3 0 0 1 0 4.243A3 3 0 1 1 13.88 1.88a3 3 0 0 1 4.242 0Z" /></svg>
                    Note
                </button>
            )}

            <div
                className='col-2 px-2 py-2 to-item__idcell'
                style={{
                    backgroundColor: backgroundColor,
                    borderRight: '1px solid #EAECF',
                }}
                onMouseDown={() => handleLongPress(index)}
                onMouseUp={handleCancelLongPress}
                onMouseLeave={handleCancelLongPress}
                onTouchStart={() => handleLongPress(index)}
                onTouchEnd={handleCancelLongPress}
            >
                <div className='d-flex gap-2 align-items-center'>
                    {
                        displayCheckbox &&
                        <input
                            type='checkbox'
                            checked={checkboxState}
                            onClick={() => { if (displayCheckbox) onToggleCheckbox(); }}
                        />
                    }
                    <span className='to-item__num' style={{ fontSize: '14px' }}>{prod.product.plu}</span>
                </div>
            </div>
            <div className='col-2 px-2 py-2' style={{ backgroundColor: backgroundColor }}
                onMouseDown={() => handleLongPress(index)} onMouseUp={handleCancelLongPress} onMouseLeave={handleCancelLongPress}
                onTouchStart={() => handleLongPress(index)}
                onTouchEnd={handleCancelLongPress}>
                <span className='to-item__num to-item__qty' style={{ fontSize: '14px' }}>{prod.qty}</span>
            </div>
            <div className='col-6 px-2 py-2' style={{ backgroundColor: backgroundColor }} onMouseDown={() => handleLongPress(index)} onMouseUp={handleCancelLongPress} onMouseLeave={handleCancelLongPress}
                onTouchStart={() => handleLongPress(index)}
                onTouchEnd={handleCancelLongPress}>
                <div className='d-flex align-items-center gap-1'>
                    <span className={'to-item__name' + (prod.sentToKitchen ? ' to-item__name--sent' : '')} style={{ fontSize: '14px', fontWeight: '500', color: prod.sentToKitchen ? '#1643E0' : '' }}>
                        {prod.name.length > 25 ? `${prod.name.slice(0, 25)}...` : prod.name}
                    </span>
                    {mealPlanStatus === 'included' && (
                        <span className='to-item__chip to-item__chip--mp' style={{
                            fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
                            backgroundColor: '#ECFDF3', color: '#027A48',
                        }}>MP</span>
                    )}
                    {mealPlanStatus === 'extra' && (
                        <span className='to-item__chip to-item__chip--extra' style={{
                            fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
                            backgroundColor: '#FEF3F2', color: '#B42318',
                        }}>EXTRA</span>
                    )}
                </div>
                {
                    prod.addons && prod.addons.length > 0 &&
                    <>
                        <span className='to-item__sub' style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>
                            Add-ons:
                        </span>
                        {
                            prod.addons.map(addon =>
                                <>
                                    {addon.qty > 1 ?
                                        <span className='to-item__sub' style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>
                                            {addon.qty} x {addon.addon?.name}
                                        </span>
                                        :
                                        <span className='to-item__sub' style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>
                                            {addon.addon?.name}
                                        </span>
                                    }

                                </>
                            )
                        }
                    </>
                }
                {
                    prod.variants && prod.variants.length > 0 &&
                    <>
                        <span className='to-item__sub' style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>
                            Variants:
                        </span>
                        {
                            prod.variants.map(variant =>
                                <>
                                    <span className='to-item__sub' style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>
                                        {variant.name}
                                    </span>
                                </>
                            )
                        }
                    </>
                }
                {
                    getDiscountValue(prod) != 0 &&
                    <>
                        <span className='to-item__sub' style={{ fontSize: '14px', display: 'flex' }}>
                            {prod.discountName || 'Discount'}
                        </span>
                    </>
                }
                {
                    prod.note != '' &&
                    <>
                        <span className='to-item__sub to-item__note' style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>Note: {prod.note}</span>
                    </>
                }
            </div>
            <div className='px-2 py-2 col d-flex flex-column ' style={{ backgroundColor: backgroundColor }} onMouseDown={() => handleLongPress(index)} onMouseUp={handleCancelLongPress}
                onTouchStart={() => handleLongPress(index)}
                onTouchEnd={handleCancelLongPress} onMouseLeave={handleCancelLongPress} >
                <span className='to-item__price' style={{ fontSize: '14px', textAlign: 'end', color: mealPlanStatus === 'included' ? '#027A48' : undefined }}>
                    {mealPlanStatus === 'included'
                        ? formatPrice(0)
                        : getDiscountValue(prod) > 0
                            ? formatPrice((getDisplayUnitPrice(prod) - getDiscountValue(prod)) * (prod.qty || 1))
                            : formatPrice(getDisplayUnitPrice(prod) * (prod.qty || 1))}
                </span>
                {
                    prod.addons && prod.addons.length > 0 &&
                    <>
                        <span style={{ fontSize: '14px', textAlign: 'end', color: 'transparent', display: 'flex' }}>
                            Add-ons:
                        </span>
                        {
                            prod.addons.map(addon =>
                                <>
                                    {
                                        addon.addon?.priceWithoutTax && addon.addon?.priceWithoutTax != 0 ?
                                            <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex', justifyContent: 'end' }}>{formatPrice(addon.addon?.priceWithoutTax * addon.qty)}</span>
                                            :
                                            <span style={{ fontSize: '14px', color: 'transparent', display: 'flex', justifyContent: 'end' }}>{getCurrencySymbol()} </span>
                                    }
                                </>
                            )
                        }
                    </>
                }
                {
                    getDiscountValue(prod) != 0 &&
                    <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex', justifyContent: 'end', whiteSpace: 'nowrap' }}>{`-${formatPrice(getDiscountValueFormatted(prod))}`}</span>
                }
            </div>

            {showDelete[index] && (
                <button
                    className='tia-reveal tia-reveal--del'
                    onClick={() => {
                        setShowDelete((prev) => ({ ...prev, [index]: false }));
                        onDelete(index);
                    }}
                    style={{ right: -63 }}
                >
                    <svg width="18" height="18" viewBox="0 0 20 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 5V4.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C12.48 1 11.92 1 10.8 1H9.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C6 2.52 6 3.08 6 4.2V5M8 10.5v5M12 10.5v5M1 5h18M17 5v11.2c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C14.72 21 13.88 21 12.2 21H7.8c-1.68 0-2.52 0-3.162-.327a3 3 0 0 1-1.311-1.311C3 18.72 3 17.88 3 16.2V5" /></svg>
                    Delete
                </button>
            )}
        </div>
    );
};
export default SwipeableOrderItem;

